import type { CapitalReport } from './types';

export const G2_REPORTS: CapitalReport[] = [
  {
    key: 'G2R1', group: 'G2', rCode: 'R1', domain: 'Product Readiness',
    stage: 'Concept Stage (Validation Gap)', tone: 'red', profile: 'Vision Builder',
    intro: "This report evaluates your startup's ability to transform an idea into a validated, testable, and scalable product.",
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You are at the earliest stage of product development, where your vision is defined but has not yet been translated into a testable or validated solution.' },
        { kind: 'paragraph', text: 'At this stage, your startup is not yet validated, meaning:' },
        { kind: 'bullets', items: ['no prototype exists', 'no real user feedback has been collected', 'no proof of demand has been established'] },
        { kind: 'paragraph', text: 'This is a critical phase where most ideas fail, not because they are bad, but because they are not tested against real users.' },
        { kind: 'callout', text: 'Your objective is not to build a full product yet' },
        { kind: 'callout', text: 'It is to validate that the problem is real, urgent, and worth solving' },
      ]},
      { title: 'Product Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Result'], rows: [
          [{ text: 'Readiness Level' }, { text: 'Concept Stage', tone: 'red' }],
          [{ text: 'Product Status' }, { text: 'Idea Only' }],
          [{ text: 'Validation Status' }, { text: 'Not Validated', tone: 'red' }],
          [{ text: 'Risk Level' }, { text: 'Critical', tone: 'red' }],
          [{ text: 'Probability of Success' }, { text: 'Very Low (<10%) under current conditions' }],
          [{ text: 'Estimated Time to Validation' }, { text: '4–8 weeks' }],
        ]},
      ]},
      { title: 'Product Gap Analysis', blocks: [
        { kind: 'paragraph', text: 'What a Valid Startup Needs vs Your Current Position' },
        { kind: 'table', headers: ['Area', 'Your Current Status', 'Required for Validation'], rows: [
          [{ text: 'Product' }, { text: 'No prototype', tone: 'red' }, { text: 'Simple prototype or mockup' }],
          [{ text: 'Users' }, { text: 'No validation', tone: 'red' }, { text: '15–20 real user interviews' }],
          [{ text: 'Problem' }, { text: 'Assumed', tone: 'orange' }, { text: 'Proven and documented' }],
          [{ text: 'Solution' }, { text: 'Conceptual', tone: 'orange' }, { text: 'Testable version' }],
          [{ text: 'Team' }, { text: 'Undefined', tone: 'orange' }, { text: 'Basic execution capability' }],
        ]},
      ]},
      { title: 'Product Readiness Breakdown', blocks: [
        { kind: 'table', headers: ['Dimension', 'Status', 'Interpretation'], rows: [
          [{ text: 'Problem Validation' }, { text: 'Weak', tone: 'red' }, { text: 'Based on assumptions, not real data' }],
          [{ text: 'Product' }, { text: 'Missing', tone: 'red' }, { text: 'No prototype or testable version' }],
          [{ text: 'User Insight' }, { text: 'None', tone: 'red' }, { text: 'No structured feedback or learning loop' }],
          [{ text: 'Execution' }, { text: 'Early', tone: 'red' }, { text: 'No clear plan to build or test' }],
        ]},
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'paragraph', text: 'Your current position is driven by:' },
        { kind: 'bullets', items: ['A defined idea without a testable product', 'No structured user validation or feedback process', 'Assumptions about the problem not yet verified', 'No clear execution plan (tools, team, or approach)'] },
        { kind: 'callout', text: 'This place you in the "Vision → Reality Gap", where ideas must be tested before being built.' },
      ]},
      { title: 'Key Gaps Identified', blocks: [
        { kind: 'steps', items: [
          { title: 'No Problem Validation', sub: ['users truly experience this problem', 'the problem is frequent and urgent', 'users are willing to pay for a solution'] },
          { title: 'No Prototype or Testable Product', sub: ['Users and stakeholders cannot interact with your idea, making feedback impossible.'] },
          { title: 'No User Feedback Loop', sub: ['test', 'learn', 'iterate'] },
          { title: 'No Execution Plan', sub: ['what to build', 'how to build it', 'who will execute'] },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 30–45 Days)', blocks: [
        { kind: 'steps', items: [
          { title: 'Validate the Problem (CRITICAL)', sub: ['Conduct 15–20 customer interviews.', 'Document: pain points; frequency; urgency.', 'Define: what success looks like for users'] },
          { title: 'Build a Simple Prototype (No-Code First)', sub: ['Use tools such as: Figma; Webflow; Bubble.', 'Create: wireframes; clickable mockups.', 'Focus on testing, not perfection'] },
          { title: 'Define MVP Scope', sub: ['Identify: must-have features; nice-to-have features.', 'Focus on 1–2 core features only'] },
          { title: 'Create a Basic Execution Plan', sub: ['Choose: No-code; Freelancer; Technical co-founder.', 'Define: timeline; cost; milestones'] },
        ]},
      ]},
      { title: 'What This Means for You', blocks: [
        { kind: 'paragraph', text: 'You are not building a company yet. You are testing a hypothesis' },
        { kind: 'paragraph', text: 'At this stage: ideas do not create value; validation creates value' },
        { kind: 'paragraph', text: 'Strong founders: test fast; learn fast; iterate fast' },
        { kind: 'callout', text: 'Your objective is to move from: Idea → Proof → Product' },
      ]},
      { title: 'Your Path to Product Readiness', blocks: [
        { kind: 'path', steps: ['Concept Stage', 'Prototype Stage', 'Validation Stage', 'Product-Market Fit'], currentIndex: 0 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: MVP Builder Package (Starter / Growth)' },
        { kind: 'paragraph', text: 'This will help you:' },
        { kind: 'bullets', items: ['validate your idea with real users', 'build your first prototype', 'define your MVP clearly', 'prepare for early traction'] },
      ]},
      { title: 'Immediate Actions', blocks: [
        { kind: 'bullets', items: ['Conduct 15+ customer interviews this month', 'Build your first clickable prototype', 'Define your MVP scope', 'Start testing your solution immediately'] },
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: 'Stop planning. Start validating your idea with real users', tone: 'green' },
      ]},
    ],
  },
  {
    key: 'G2R2', group: 'G2', rCode: 'R2', domain: 'Product Readiness',
    stage: 'Prototype Stage (Early Validation Gap)', tone: 'red', profile: 'Prototype Explorer',
    intro: "This report evaluates your startup's ability to transform an idea into a testable and validated product.",
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You have progressed beyond the idea stage and taken the first step toward building your product by creating an initial prototype or concept.' },
        { kind: 'paragraph', text: 'However, your startup is still in the early validation phase, where assumptions have not yet been tested with real users at scale.' },
        { kind: 'paragraph', text: 'At this stage: a prototype may exist; initial thinking is structured; but validation remains limited or inconsistent' },
        { kind: 'callout', text: 'You are no longer just ideating' },
        { kind: 'callout', text: 'You are now testing, but not yet proving' },
        { kind: 'paragraph', text: 'Your objective is to: validate that users truly need your solution and are willing to engage' },
      ]},
      { title: 'Product Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Result'], rows: [
          [{ text: 'Readiness Level' }, { text: 'Prototype Stage', tone: 'red' }],
          [{ text: 'Product Status' }, { text: 'Early Prototype / Mockup' }],
          [{ text: 'Validation Status' }, { text: 'Limited Validation', tone: 'orange' }],
          [{ text: 'Risk Level' }, { text: 'High', tone: 'red' }],
          [{ text: 'Probability of Success' }, { text: 'Low (10–20%) under current conditions' }],
          [{ text: 'Estimated Time to Validation' }, { text: '3–6 weeks' }],
        ]},
      ]},
      { title: 'Product Gap Analysis', blocks: [
        { kind: 'paragraph', text: 'What a Valid Startup Needs vs Your Current Position' },
        { kind: 'table', headers: ['Area', 'Your Current Status', 'Required for Validation'], rows: [
          [{ text: 'Product' }, { text: 'Basic prototype exists', tone: 'orange' }, { text: 'Testable MVP with user interaction' }],
          [{ text: 'Users' }, { text: 'Limited feedback', tone: 'orange' }, { text: '15–30 structured user interviews' }],
          [{ text: 'Problem' }, { text: 'Partially validated', tone: 'orange' }, { text: 'Proven and documented with data' }],
          [{ text: 'Solution' }, { text: 'Early version', tone: 'orange' }, { text: 'Iterated based on real feedback' }],
          [{ text: 'Team' }, { text: 'Basic setup', tone: 'orange' }, { text: 'Ability to execute and iterate quickly' }],
        ]},
      ]},
      { title: 'Product Readiness Breakdown', blocks: [
        { kind: 'table', headers: ['Dimension', 'Status', 'Interpretation'], rows: [
          [{ text: 'Problem Validation' }, { text: 'Weak', tone: 'red' }, { text: 'Limited real-world validation' }],
          [{ text: 'Product' }, { text: 'Basic', tone: 'yellow' }, { text: 'Prototype exists but not fully testable' }],
          [{ text: 'User Insight' }, { text: 'Low', tone: 'red' }, { text: 'Feedback is limited or informal' }],
          [{ text: 'Execution' }, { text: 'Early', tone: 'yellow' }, { text: 'Initial build exists but lacks iteration' }],
        ]},
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'paragraph', text: 'Your current position is driven by:' },
        { kind: 'bullets', items: ['A prototype or mockup has been created', 'Initial feedback may exist but is not structured', 'The problem is assumed rather than fully validated', 'No systematic testing or iteration process'] },
        { kind: 'callout', text: 'This place you in the "Prototype → Validation Gap", where testing must replace assumptions.' },
      ]},
      { title: 'Key Gaps Identified', blocks: [
        { kind: 'steps', items: [
          { title: 'Weak Problem Validation', sub: ['You have signals, but not proof: limited interviews; no structured insights; no clear patterns'] },
          { title: 'Non-Iterated Product', sub: ['evolved based on feedback', 'improved through testing cycles'] },
          { title: 'Limited User Feedback Loop', sub: ['test', 'measure', 'improve'] },
          { title: 'Lack of Learning Process', sub: ['No clear loop: build → test → learn → iterate'] },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 3–5 Weeks)', blocks: [
        { kind: 'steps', items: [
          { title: 'Structure User Validation', sub: ['Conduct 20–30 structured interviews.', 'Capture: pain points; behaviors; willingness to use/pay'] },
          { title: 'Improve Prototype into Testable MVP', sub: ['Upgrade prototype to: clickable flows; usable experience.', 'Focus on user interaction'] },
          { title: 'Build Feedback Loop', sub: ['Test weekly.', 'Track: engagement; drop-offs; user reactions'] },
          { title: 'Iterate Rapidly', sub: ['Run multiple product iterations', 'Improve based on real usage'] },
        ]},
      ]},
      { title: 'What This Means for You', blocks: [
        { kind: 'paragraph', text: 'You are no longer guessing. You are testing reality' },
        { kind: 'paragraph', text: 'At this stage: ideas start to face real-world friction; assumptions are challenged' },
        { kind: 'paragraph', text: 'Strong founders: test continuously; adapt quickly; iterate aggressively' },
        { kind: 'callout', text: 'Your objective is to move from: Concept → Prototype → Validated Product' },
      ]},
      { title: 'Your Path to Product Readiness', blocks: [
        { kind: 'path', steps: ['Concept Stage', 'Prototype Stage', 'Validation Stage', 'Product-Market Fit'], currentIndex: 1 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: MVP Builder Package (Validation Focus)' },
        { kind: 'paragraph', text: 'This will help you:' },
        { kind: 'bullets', items: ['structure user testing', 'improve your prototype', 'validate demand', 'build early traction'] },
      ]},
      { title: 'Immediate Actions', blocks: [
        { kind: 'bullets', items: ['Conduct 20+ structured user interviews', 'Improve prototype usability', 'Start weekly testing cycles', 'Document learnings and iterate'] },
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: 'Stop assuming. Start testing your product with real users', tone: 'green' },
      ]},
    ],
  },
  {
    key: 'G2R3', group: 'G2', rCode: 'R3', domain: 'Product Readiness',
    stage: 'MVP Stage (Validation Structuring Phase)', tone: 'yellow', profile: 'MVP Builder',
    intro: "This report evaluates your startup's ability to transform your MVP into a validated, scalable, and investable product.",
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You have reached a critical milestone in product development. You are now building or operating an MVP.' },
        { kind: 'paragraph', text: 'This means you have moved beyond: idea; prototype' },
        { kind: 'paragraph', text: 'And entered: real-world product validation' },
        { kind: 'paragraph', text: 'However, your MVP is not yet structured for effective validation.' },
        { kind: 'paragraph', text: 'At this stage, the main risk is not failure, it is wasted effort due to lack of focus and measurement' },
        { kind: 'paragraph', text: 'Without discipline, founders often: overbuild features; ignore user signals; delay validation' },
        { kind: 'callout', text: 'Your objective is not to build more' },
        { kind: 'callout', text: 'It is to build the right thing and measure it correctly' },
      ]},
      { title: 'Product Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Result'], rows: [
          [{ text: 'Readiness Level' }, { text: 'MVP Stage', tone: 'yellow' }],
          [{ text: 'Product Status' }, { text: 'MVP in Progress' }],
          [{ text: 'Validation Status' }, { text: 'Weak / Unstructured', tone: 'orange' }],
          [{ text: 'Risk Level' }, { text: 'Medium–High', tone: 'yellow' }],
          [{ text: 'Probability of Success' }, { text: 'Moderate (25–45%) under current conditions' }],
          [{ text: 'Estimated Time to Validation' }, { text: '4–6 weeks' }],
        ]},
      ]},
      { title: 'Product Gap Analysis', blocks: [
        { kind: 'paragraph', text: 'What a Strong MVP Requires vs Your Current Position' },
        { kind: 'table', headers: ['Area', 'Your Current Status', 'Required for Validation'], rows: [
          [{ text: 'MVP Scope' }, { text: 'Too broad / unclear', tone: 'orange' }, { text: 'Focused (1–2 core features)' }],
          [{ text: 'Feature Set' }, { text: 'Unstructured', tone: 'orange' }, { text: 'Prioritized for validation' }],
          [{ text: 'Metrics' }, { text: 'Undefined', tone: 'red' }, { text: 'Clear KPIs (usage, retention, conversion)' }],
          [{ text: 'User Testing' }, { text: 'Limited', tone: 'orange' }, { text: '20–30 structured users' }],
          [{ text: 'Feedback Loop' }, { text: 'Missing', tone: 'red' }, { text: 'Continuous iteration system' }],
        ]},
      ]},
      { title: 'Product Readiness Breakdown', blocks: [
        { kind: 'table', headers: ['Dimension', 'Status', 'Interpretation'], rows: [
          [{ text: 'Product' }, { text: 'Building', tone: 'yellow' }, { text: 'MVP exists or in progress' }],
          [{ text: 'Focus' }, { text: 'Weak', tone: 'red' }, { text: 'Too many features / lack of prioritization' }],
          [{ text: 'Validation' }, { text: 'Partial', tone: 'yellow' }, { text: 'Early signals but not structured' }],
          [{ text: 'Execution' }, { text: 'Moderate', tone: 'yellow' }, { text: 'Needs discipline and measurement' }],
        ]},
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'paragraph', text: 'Your current position is driven by:' },
        { kind: 'bullets', items: ['MVP is being built but lacks clear scope and prioritization', 'Features may be excessive or not aligned with core value', 'Early users may exist, but feedback is not structured', 'No clear definition of success metrics'] },
        { kind: 'callout', text: 'This place you in the "Build → Validate Discipline Gap", where success depends on focus and structured learning.' },
      ]},
      { title: 'Key Gaps Identified', blocks: [
        { kind: 'steps', items: [
          { title: 'Lack of Focus', sub: ['Too many features dilute value and slow validation.', 'Your MVP must answer one question: "Does this solve the core problem?"'] },
          { title: 'No Defined Success Metrics', sub: ['usage', 'retention', 'engagement', 'willingness to pay'] },
          { title: 'Weak Feedback System', sub: ['test', 'learn', 'iterate'] },
          { title: 'Risk of Overbuilding', sub: ['Time is being spent on: features, instead of: validation and learning'] },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 3–5 Weeks)', blocks: [
        { kind: 'steps', items: [
          { title: 'Define MVP Scope (CRITICAL)', sub: ['Identify: must-have features; nice-to-have features.', 'Focus on 1–2 core features only'] },
          { title: 'Define Success Metrics', sub: ['Track from day one: signups; daily usage; retention; willingness to pay.', 'Use simple tools: Google Sheets; Airtable'] },
          { title: 'Build Structured Feedback Loop', sub: ['Recruit 20–30 target users.', 'Run weekly feedback sessions.', 'Track: usability; friction; value perception'] },
          { title: 'Iterate Rapidly', sub: ['Improve usability; simplify flows; remove unnecessary features; focus on user value'] },
        ]},
      ]},
      { title: 'What This Means for You', blocks: [
        { kind: 'paragraph', text: 'You are in one of the most critical and risky phases of startup building:' },
        { kind: 'paragraph', text: 'Where many founders: build too much; validate too little' },
        { kind: 'paragraph', text: 'Winning founders: focus; measure; iterate' },
        { kind: 'callout', text: 'Your objective is to move from: MVP → Validated Product' },
      ]},
      { title: 'Your Path to Product Readiness', blocks: [
        { kind: 'path', steps: ['Prototype Stage', 'MVP Stage', 'Validation Stage', 'Product-Market Fit'], currentIndex: 1 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: MVP Builder / Product Validation Package' },
        { kind: 'paragraph', text: 'This will help you:' },
        { kind: 'bullets', items: ['define MVP scope correctly', 'implement validation metrics', 'build structured feedback loops', 'avoid overbuilding'] },
      ]},
      { title: 'Immediate Actions', blocks: [
        { kind: 'bullets', items: ['Reduce MVP to 1–2 core features', 'Define and track KPIs', 'Recruit 20+ users', 'Start weekly validation cycles'] },
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: "Stop building more. Start validating what you've built", tone: 'green' },
      ]},
    ],
  },
  {
    key: 'G2R4', group: 'G2', rCode: 'R4', domain: 'Product Readiness',
    stage: 'Launch Stage (Market Validation Phase)', tone: 'yellow', profile: 'Product Launcher',
    intro: "This report evaluates your startup's ability to successfully launch your product and validate real market demand and early traction.",
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You have reached a critical inflection point: your MVP is functional and ready to meet the market.' },
        { kind: 'paragraph', text: 'At this stage, the focus shifts from: building; features; development' },
        { kind: 'paragraph', text: 'To: real market validation and traction generation' },
        { kind: 'paragraph', text: 'You are now entering: The Market Test Phase' },
        { kind: 'paragraph', text: 'However, the primary risk is no longer the product itself, it is how you launch, measure, and iterate' },
        { kind: 'paragraph', text: 'Without a structured launch: traction will be unclear; insights will be lost; growth will stall' },
        { kind: 'callout', text: 'Your objective is not just to launch' },
        { kind: 'callout', text: 'It is to generate measurable traction and validated learning' },
      ]},
      { title: 'Product Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Result'], rows: [
          [{ text: 'Readiness Level' }, { text: 'Launch Stage', tone: 'yellow' }],
          [{ text: 'Product Status' }, { text: 'MVP Ready' }],
          [{ text: 'Market Validation' }, { text: 'Early / Unproven', tone: 'orange' }],
          [{ text: 'Risk Level' }, { text: 'Medium', tone: 'yellow' }],
          [{ text: 'Probability of Success' }, { text: 'Moderate (40–60%) under current conditions' }],
          [{ text: 'Estimated Time to Validation' }, { text: '4–8 weeks' }],
        ]},
      ]},
      { title: 'Product Gap Analysis', blocks: [
        { kind: 'paragraph', text: 'What a Successful Launch Requires vs Your Current Position' },
        { kind: 'table', headers: ['Area', 'Your Current Status', 'Required for Success'], rows: [
          [{ text: 'MVP' }, { text: 'Built & functional', tone: 'green' }, { text: 'Must be tested with real users' }],
          [{ text: 'Users' }, { text: 'Pilot / early users', tone: 'orange' }, { text: '50–100 structured users' }],
          [{ text: 'GTM Strategy' }, { text: 'Missing / unclear', tone: 'red' }, { text: 'Defined launch plan' }],
          [{ text: 'Metrics' }, { text: 'Partial', tone: 'orange' }, { text: 'Clear KPI tracking system' }],
          [{ text: 'Traction' }, { text: 'Early signals', tone: 'orange' }, { text: 'Consistent engagement & growth' }],
        ]},
      ]},
      { title: 'Product Readiness Breakdown', blocks: [
        { kind: 'table', headers: ['Dimension', 'Status', 'Interpretation'], rows: [
          [{ text: 'Product' }, { text: 'Ready', tone: 'green' }, { text: 'MVP is functional and usable' }],
          [{ text: 'Validation' }, { text: 'Early', tone: 'yellow' }, { text: 'Needs structured testing and tracking' }],
          [{ text: 'GTM Strategy' }, { text: 'Weak', tone: 'red' }, { text: 'No defined launch or acquisition plan' }],
          [{ text: 'Traction' }, { text: 'Limited', tone: 'yellow' }, { text: 'Engagement is not yet consistent' }],
        ]},
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'paragraph', text: 'Your current position is driven by:' },
        { kind: 'bullets', items: ['A functional MVP ready for real users', 'Early signals such as signups or initial engagement', 'Lack of structured go-to-market execution', 'No consistent system for tracking and optimizing traction'] },
        { kind: 'callout', text: 'This place you in the "Launch → Traction Gap", where execution determines outcomes.' },
      ]},
      { title: 'Key Gaps Identified', blocks: [
        { kind: 'steps', items: [
          { title: 'No Structured Launch Strategy', sub: ['clear plan', 'defined channels', 'consistent messaging'] },
          { title: 'Weak Go-To-Market (GTM) Strategy', sub: ['precise target audience', 'acquisition channels', 'testing strategy'] },
          { title: 'Inconsistent User Engagement', sub: ['are not consistently active', 'are not retained'] },
          { title: 'No Traction System', sub: ['tracking KPIs', 'running iteration cycles', 'building growth loops'] },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 4–8 Weeks)', blocks: [
        { kind: 'steps', items: [
          { title: 'Execute Structured Beta Launch (CRITICAL)', sub: ['Recruit 50–100 target users.', 'Define: activation rate; retention; NPS.', 'Analyze: cohort behavior; drop-offs'] },
          { title: 'Build Go-To-Market Strategy', sub: ['Select your first acquisition channel: organic communities; paid acquisition; partnerships.', 'Define messaging: problem; solution; why now'] },
          { title: 'Track & Optimize Traction', sub: ['Monitor daily: usage; engagement.', 'Run weekly: product improvements.', 'Start collecting: testimonials; case studies'] },
          { title: 'Build Early Growth Loop', sub: ['Identify: how users discover your product; what makes them return.', 'Optimize: acquisition → activation → retention'] },
        ]},
      ]},
      { title: 'What This Means for You', blocks: [
        { kind: 'paragraph', text: 'You are no longer building a product. You are testing a business model' },
        { kind: 'paragraph', text: 'At this stage: traction > features; learning > perfection' },
        { kind: 'paragraph', text: 'Strong founders: launch fast; measure everything; iterate continuously' },
        { kind: 'callout', text: 'Your objective is to move from: Launch → Traction → Validation' },
      ]},
      { title: 'Your Path to Product Readiness', blocks: [
        { kind: 'path', steps: ['MVP Stage', 'Launch Stage', 'Validation Stage', 'Product-Market Fit'], currentIndex: 1 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: Growth & GTM Strategy Package' },
        { kind: 'paragraph', text: 'This will help you:' },
        { kind: 'bullets', items: ['structure your launch', 'define your go-to-market strategy', 'build a traction system', 'accelerate validation'] },
      ]},
      { title: 'Immediate Actions', blocks: [
        { kind: 'bullets', items: ['Launch beta with 50–100 users', 'Define your primary acquisition channel', 'Track engagement daily', 'Run weekly iteration cycles'] },
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: "Don't just launch. Launch with a system that generates traction", tone: 'green' },
      ]},
    ],
  },
  {
    key: 'G2R5', group: 'G2', rCode: 'R5', domain: 'Product Readiness',
    stage: 'Validation Stage (Traction Optimization Phase)', tone: 'green', profile: 'Validated Builder',
    intro: "This report evaluates your startup's ability to convert early traction into a repeatable and scalable growth engine.",
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You have crossed one of the most difficult thresholds in startup building:' },
        { kind: 'paragraph', text: 'Your product is no longer theoretical' },
        { kind: 'paragraph', text: 'It is validated through real user behavior' },
        { kind: 'paragraph', text: 'This means: users are actively engaging with your product; value is being delivered; early traction signals are visible' },
        { kind: 'paragraph', text: 'However, validation is not the finish line, it is the starting point of scaling' },
        { kind: 'paragraph', text: 'You are now entering: The Traction Optimization Phase' },
        { kind: 'paragraph', text: 'At this stage, the key question becomes:' },
        { kind: 'callout', text: 'Can your growth become predictable and repeatable?' },
      ]},
      { title: 'Product Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Result'], rows: [
          [{ text: 'Readiness Level' }, { text: 'Validation Stage', tone: 'green' }],
          [{ text: 'Product Status' }, { text: 'Validated MVP' }],
          [{ text: 'Traction' }, { text: 'Early / Inconsistent', tone: 'yellow' }],
          [{ text: 'Risk Level' }, { text: 'Medium', tone: 'yellow' }],
          [{ text: 'Probability of Success' }, { text: 'High (60–75%) under current conditions' }],
          [{ text: 'Estimated Time to Scale' }, { text: '6–10 weeks' }],
        ]},
      ]},
      { title: 'Product Gap Analysis', blocks: [
        { kind: 'paragraph', text: 'What a Scalable Startup Requires vs Your Current Position' },
        { kind: 'table', headers: ['Area', 'Your Current Status', 'Required for Scale'], rows: [
          [{ text: 'Product' }, { text: 'Valuable', tone: 'green' }, { text: 'Needs optimization & stability' }],
          [{ text: 'Users' }, { text: 'Active', tone: 'green' }, { text: 'Must grow consistently' }],
          [{ text: 'Retention' }, { text: 'Weak / unclear', tone: 'orange' }, { text: 'Strong retention & low churn' }],
          [{ text: 'GTM' }, { text: 'Inconsistent', tone: 'orange' }, { text: 'Repeatable acquisition engine' }],
          [{ text: 'Metrics' }, { text: 'Partial', tone: 'yellow' }, { text: 'Full KPI system (CAC, LTV, MRR/ARR)' }],
        ]},
      ]},
      { title: 'Product Readiness Breakdown', blocks: [
        { kind: 'table', headers: ['Dimension', 'Status', 'Interpretation'], rows: [
          [{ text: 'Product' }, { text: 'Validated', tone: 'green' }, { text: 'Users recognize and experience value' }],
          [{ text: 'Retention' }, { text: 'Weak', tone: 'yellow' }, { text: 'Users are not consistently retained' }],
          [{ text: 'GTM' }, { text: 'Early', tone: 'yellow' }, { text: 'Channels exist but are not repeatable' }],
          [{ text: 'Growth' }, { text: 'Limited', tone: 'yellow' }, { text: 'Not yet scalable or predictable' }],
        ]},
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'paragraph', text: 'Your current position is driven by:' },
        { kind: 'bullets', items: ['Active users engaging with your MVP', 'Early traction signals (usage, engagement, or revenue)', 'A product that delivers value but still has UX or consistency gaps', 'Go-to-market efforts that work intermittently but lack structure'] },
        { kind: 'callout', text: 'This place you in the "Validation → Scale Gap", where systems must replace experimentation.' },
      ]},
      { title: 'Key Gaps Identified', blocks: [
        { kind: 'steps', items: [
          { title: 'Weak Retention System', sub: ['Users try your product, but do not consistently return.', 'Retention is not yet optimized'] },
          { title: 'Non-Repeatable Growth', sub: ['Acquisition works in bursts, not as a system.', 'Growth lacks predictability'] },
          { title: 'Missing Core Growth Metrics', sub: ['CAC (Customer Acquisition Cost)', 'LTV (Lifetime Value)', 'MRR / ARR. Without metrics, scaling decisions are blind'] },
          { title: 'No Scaling Infrastructure', sub: ['structured onboarding', 'growth playbook', 'operational systems'] },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 4–8 Weeks)', blocks: [
        { kind: 'steps', items: [
          { title: 'Strengthen Retention (CRITICAL)', sub: ['Identify: where users drop off.', 'Improve: onboarding; UX; core value delivery.', 'Focus on stickiness and repeat usage'] },
          { title: 'Build Repeatable GTM Playbook', sub: ['Identify: which channels work.', 'Standardize: acquisition process.', 'Test: pricing; messaging.', 'Goal: predictable growth engine'] },
          { title: 'Translate Traction into Metrics', sub: ['Track: CAC; LTV; MRR / ARR.', 'This is investor language and scaling foundation'] },
          { title: 'Prepare for Scaling & Fundraising', sub: ['Build roadmap across: product; hiring; operations.', 'Upgrade pitch: include traction metrics; show growth trends'] },
        ]},
      ]},
      { title: 'What This Means for You', blocks: [
        { kind: 'paragraph', text: 'You have significantly de-risked your startup: Customers care; Product works' },
        { kind: 'paragraph', text: 'Now the challenge becomes: Can this scale efficiently?' },
        { kind: 'paragraph', text: 'At this stage: good founders validate; great founders build systems' },
        { kind: 'callout', text: 'Your objective is to move from: Validation → Repeatable Growth → Scale' },
      ]},
      { title: 'Your Path to Product Readiness', blocks: [
        { kind: 'path', steps: ['Launch Stage', 'Validation Stage', 'Scalable Stage', 'Product-Market Fit'], currentIndex: 1 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: Growth & Scaling Package' },
        { kind: 'paragraph', text: 'This will help you:' },
        { kind: 'bullets', items: ['improve retention', 'build repeatable acquisition', 'structure growth metrics', 'prepare for fundraising'] },
      ]},
      { title: 'Immediate Actions', blocks: [
        { kind: 'bullets', items: ['Analyze retention and drop-offs this week', 'Document your GTM channels', 'Start tracking CAC / LTV / MRR', 'Upgrade your pitch with traction data'] },
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: 'Turn early traction into predictable, scalable growth', tone: 'green' },
      ]},
    ],
  },
  {
    key: 'G2R6', group: 'G2', rCode: 'R6', domain: 'Product Readiness',
    stage: 'Scalable Stage (Growth Engine Phase)', tone: 'green', profile: 'Scalable Builder',
    intro: "This report evaluates your startup's ability to scale product, traction, and growth systems into a repeatable, efficient, and investable business.",
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You have successfully moved beyond validation: Your product works; Customers are paying; Traction is consistent' },
        { kind: 'paragraph', text: 'This confirms that: the core product risk is resolved' },
        { kind: 'paragraph', text: 'The key question now becomes: Can this business scale efficiently and predictably?' },
        { kind: 'paragraph', text: 'You are now entering: The Growth Engine Phase' },
        { kind: 'paragraph', text: 'This is the transition from: early traction, to: structured, scalable growth' },
        { kind: 'paragraph', text: 'At this stage, success is no longer about experimentation, it is about systems, efficiency, and execution discipline' },
      ]},
      { title: 'Product Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Result'], rows: [
          [{ text: 'Readiness Level' }, { text: 'Scalable Stage', tone: 'green' }],
          [{ text: 'Product Status' }, { text: 'Validated & Growing' }],
          [{ text: 'Traction' }, { text: 'Consistent', tone: 'green' }],
          [{ text: 'Risk Level' }, { text: 'Medium', tone: 'yellow' }],
          [{ text: 'Probability of Success' }, { text: 'High (70–85%) under current conditions' }],
          [{ text: 'Investor Readiness' }, { text: 'Strong', tone: 'green' }],
          [{ text: 'Estimated Time to Scale' }, { text: '8–16 weeks' }],
        ]},
      ]},
      { title: 'Product Gap Analysis', blocks: [
        { kind: 'paragraph', text: 'What a Growth Company Requires vs Your Current Position' },
        { kind: 'table', headers: ['Area', 'Your Current Status', 'Required for Scale'], rows: [
          [{ text: 'Product' }, { text: 'Stable', tone: 'green' }, { text: 'Must handle growth volume reliably' }],
          [{ text: 'Users' }, { text: 'Active & paying', tone: 'green' }, { text: 'Must grow consistently and predictably' }],
          [{ text: 'Unit Economics' }, { text: 'Basic', tone: 'orange' }, { text: 'Must be optimized and efficient' }],
          [{ text: 'GTM' }, { text: 'Semi-repeatable', tone: 'yellow' }, { text: 'Fully repeatable acquisition engine' }],
          [{ text: 'Infrastructure' }, { text: 'Growing', tone: 'orange' }, { text: 'Scalable and resilient systems' }],
        ]},
      ]},
      { title: 'Product Readiness Breakdown', blocks: [
        { kind: 'table', headers: ['Dimension', 'Status', 'Interpretation'], rows: [
          [{ text: 'Product' }, { text: 'Strong', tone: 'green' }, { text: 'Proven and delivering value' }],
          [{ text: 'Growth' }, { text: 'Moderate', tone: 'yellow' }, { text: 'Requires scaling discipline' }],
          [{ text: 'Unit Economics' }, { text: 'Basic', tone: 'yellow' }, { text: 'Needs optimization and control' }],
          [{ text: 'Systems' }, { text: 'Developing', tone: 'yellow' }, { text: 'Not yet fully scalable' }],
        ]},
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'paragraph', text: 'Your current position is driven by:' },
        { kind: 'bullets', items: ['A validated MVP with paying users', 'Early repeatable acquisition channels', 'Improving engagement, but not fully optimized', 'Initial financial metrics (CAC, LTV, runway) tracked'] },
        { kind: 'callout', text: 'This place you in the "Growth → Scale Discipline Gap", where systems must replace manual execution.' },
      ]},
      { title: 'Key Gaps Identified', blocks: [
        { kind: 'steps', items: [
          { title: 'Limited Unit Economics Optimization', sub: ['You are tracking metrics, but not actively optimizing: CAC; LTV; payback period'] },
          { title: 'Scaling Infrastructure Risk', sub: ['Your product and systems may not sustain rapid growth: performance; reliability; automation'] },
          { title: 'Semi-Structured GTM', sub: ['not fully predictable', 'not fully scalable'] },
          { title: 'Missing Growth Systems', sub: ['documented processes', 'automation', 'operational scalability'] },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 6–12 Weeks)', blocks: [
        { kind: 'steps', items: [
          { title: 'Optimize Unit Economics (CRITICAL)', sub: ['Improve: CAC efficiency; LTV expansion; retention.', 'Focus on sustainable growth profitability'] },
          { title: 'Scale Product & Infrastructure', sub: ['Ensure: system stability; performance under load; scalable architecture.', 'Implement: QA systems; release processes; monitoring tools.', 'Goal: scale without breaking'] },
          { title: 'Build a Repeatable Growth Engine', sub: ['Document: sales funnel; onboarding flow; customer lifecycle.', 'Systematize: acquisition; activation; retention.', 'Goal: predictable growth machine'] },
          { title: 'Align Growth with Capital Strategy', sub: ['Allocate resources across: product; growth.', 'Build: hiring roadmap; scaling plan'] },
        ]},
      ]},
      { title: 'What This Means for You', blocks: [
        { kind: 'paragraph', text: 'You have successfully de-risked: product; demand' },
        { kind: 'paragraph', text: 'Now investors evaluate: your ability to scale efficiently' },
        { kind: 'paragraph', text: 'At this stage: good startups grow; great startups scale' },
        { kind: 'callout', text: 'Your objective is to move from: Growth → Scale → Market Leadership' },
      ]},
      { title: 'Your Path to Product Readiness', blocks: [
        { kind: 'path', steps: ['Validation Stage', 'Scalable Stage', 'Product-Market Fit', 'Market Leader'], currentIndex: 1 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: Scaling & Fundraising Preparation' },
        { kind: 'paragraph', text: 'This will help you:' },
        { kind: 'bullets', items: ['optimize unit economics', 'structure your growth engine', 'prepare for investor discussions', 'scale efficiently'] },
      ]},
      { title: 'Immediate Actions', blocks: [
        { kind: 'bullets', items: ['Optimize CAC and LTV immediately', 'Stress-test product and infrastructure', 'Document your GTM playbook', 'Build a structured growth roadmap'] },
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: 'You are ready. Now build a scalable growth engine', tone: 'green' },
      ]},
    ],
  },
  {
    key: 'G2R7', group: 'G2', rCode: 'R7', domain: 'Product Readiness',
    stage: 'Product-Market Leadership (Leverage Stage)', tone: 'green', profile: 'Product Star',
    intro: "This report evaluates your startup's ability to scale into a dominant market position and attract top-tier investors.",
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You have reached the highest level of product maturity and validation.' },
        { kind: 'paragraph', text: 'Your product is: validated; scalable; delivering strong user value; generating consistent and accelerating traction' },
        { kind: 'callout', text: 'You are no longer pursuing product-market fit' },
        { kind: 'callout', text: 'You are demonstrating market leadership potential' },
        { kind: 'paragraph', text: 'At this stage: investors do not just evaluate your company, they compete to invest' },
        { kind: 'paragraph', text: 'You are now entering: The Leverage & Expansion Phase' },
      ]},
      { title: 'Product Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Result'], rows: [
          [{ text: 'Readiness Level' }, { text: 'Product-Market Leadership', tone: 'green' }],
          [{ text: 'Product Status' }, { text: 'Scaled & Proven' }],
          [{ text: 'Traction' }, { text: 'Strong & Accelerating', tone: 'green' }],
          [{ text: 'Risk Level' }, { text: 'Low', tone: 'green' }],
          [{ text: 'Probability of Success' }, { text: 'Very High (85–95%) under current conditions' }],
          [{ text: 'Investor Demand' }, { text: 'High', tone: 'green' }],
          [{ text: 'Estimated Time to Major Raise' }, { text: '4–8 weeks' }],
        ]},
      ]},
      { title: 'Product Gap Analysis', blocks: [
        { kind: 'paragraph', text: 'What a Market Leader Requires vs Your Current Position' },
        { kind: 'table', headers: ['Area', 'Your Current Status', 'Required for Leadership'], rows: [
          [{ text: 'Product' }, { text: 'Strong', tone: 'green' }, { text: 'Must scale globally and reliably' }],
          [{ text: 'Traction' }, { text: 'Accelerating', tone: 'green' }, { text: 'Must sustain and compound growth' }],
          [{ text: 'Unit Economics' }, { text: 'Investor-grade', tone: 'green' }, { text: 'Must continuously optimize efficiency' }],
          [{ text: 'GTM' }, { text: 'Multi-channel', tone: 'green' }, { text: 'Must dominate key acquisition channels' }],
          [{ text: 'Infrastructure' }, { text: 'Stable', tone: 'green' }, { text: 'Must support international scale' }],
        ]},
      ]},
      { title: 'Product Readiness Breakdown', blocks: [
        { kind: 'table', headers: ['Dimension', 'Status', 'Interpretation'], rows: [
          [{ text: 'Product' }, { text: 'Excellent', tone: 'green' }, { text: 'Strong, differentiated user value' }],
          [{ text: 'Growth' }, { text: 'Strong', tone: 'green' }, { text: 'Accelerating and repeatable' }],
          [{ text: 'Economics' }, { text: 'Solid', tone: 'green' }, { text: 'Efficient and investor-grade' }],
          [{ text: 'Systems' }, { text: 'Mature', tone: 'green' }, { text: 'Ready for scale and expansion' }],
        ]},
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'paragraph', text: 'Your current position is driven by:' },
        { kind: 'bullets', items: ['A validated and scaling product with strong retention and engagement', 'Solid unit economics (CAC, LTV, churn)', 'Repeatable growth across multiple acquisition channels', 'Stable and scalable technology and operations', 'Early or active investor interest'] },
        { kind: 'callout', text: 'You have transitioned from startup → growth company' },
      ]},
      { title: 'Remaining Risks (Strategic Level)', blocks: [
        { kind: 'paragraph', text: 'At this level, risks are no longer operational, they are strategic:' },
        { kind: 'steps', items: [
          { title: 'Investor Selection Risk', sub: ['Choosing the wrong investors can limit long-term upside'] },
          { title: 'Over-Scaling Risk', sub: ['Scaling too aggressively can strain infrastructure and execution'] },
          { title: 'Strategic Misalignment', sub: ['Capital without strategic value reduces long-term growth potential'] },
          { title: 'Global Expansion Complexity', sub: ['Entering new markets requires: localization; compliance; operational readiness'] },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 6–12 Weeks)', blocks: [
        { kind: 'steps', items: [
          { title: 'Scale Strategically', sub: ['Expand into: new markets; new customer segments.', 'Localize: product; GTM strategy'] },
          { title: 'Maximize Growth Leverage', sub: ['Double down on: highest-performing channels.', 'Build: referral loops; partnerships; enterprise pipeline.', 'Goal: maximize ROI across all channels'] },
          { title: 'Prepare for Institutional Funding (Series A/B)', sub: ['Structure: institutional data room; governance; board setup.', 'Position the raise as strategic acceleration, not capital need'] },
        ]},
      ]},
      { title: 'What This Means for You', blocks: [
        { kind: 'paragraph', text: 'You are operating in elite territory:' },
        { kind: 'paragraph', text: 'Your product: works; scales; attracts capital' },
        { kind: 'paragraph', text: 'At this stage: investors compete; terms improve; opportunities expand' },
        { kind: 'paragraph', text: 'Your advantage is: Leverage' },
        { kind: 'paragraph', text: 'Your responsibility is: Strategic decision-making' },
        { kind: 'callout', text: 'The game shifts from: "Can we grow?" → "How do we dominate?"' },
      ]},
      { title: 'Your Path Forward', blocks: [
        { kind: 'path', steps: ['Scalable Stage', 'Product Star', 'Market Leader', 'Global Expansion'], currentIndex: 1 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: Strategic Growth & Capital Optimization' },
        { kind: 'paragraph', text: 'This will help you:' },
        { kind: 'bullets', items: ['select the right investors', 'structure your next funding round', 'expand into new markets', 'optimize long-term growth'] },
      ]},
      { title: 'Immediate Actions', blocks: [
        { kind: 'bullets', items: ['Identify expansion markets and segments', 'Build a strategic investor shortlist', 'Prepare Series A/B positioning', 'Strengthen governance and operations'] },
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: 'You have leverage. Use it to dominate your market and scale globally', tone: 'green' },
      ]},
    ],
  },
];
